import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutTemplate, Newspaper, PanelRight, FileText, TrendingUp, Upload, Zap, BarChart3, CalendarDays, RotateCcw, Clock3, Headphones } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import { startCheckout } from "@/lib/checkout";
import { NewsletterCTA } from "@/components/NewsletterCTA";
import { AvailabilityGrid } from "@/components/AvailabilityGrid";
import { SlotHeatmap } from "@/components/SlotHeatmap";
import { CampaignStats } from "@/components/CampaignStats";
import { AdSpecSheet } from "@/components/AdSpecSheet";
import { AdLivePreview } from "@/components/AdLivePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

/* Mirrors backend interp_base_price (ad_pricing.py): exact anchors, linear between,
   pro-rated below the shortest anchor, marginal-rate extrapolation above the longest. */
const interpBase = (prices: Record<string, number> | undefined, days: number): number | null => {
  if (!prices) return null;
  const pts = Object.entries(prices)
    .map(([k, v]) => [Number(k), Number(v)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  if (!pts.length || days < 1 || days > 365) return null;
  for (const [d, p] of pts) if (d === days) return p;
  const [d0, p0] = pts[0];
  if (days < d0) return Math.round((p0 * days / d0) * 100) / 100;
  const [dn, pn] = pts[pts.length - 1];
  if (days > dn) {
    const [dp, pp] = pts.length > 1 ? pts[pts.length - 2] : [0, 0];
    const marginal = dn > dp ? (pn - pp) / (dn - dp) : pn / dn;
    return Math.round((pn + marginal * (days - dn)) * 100) / 100;
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const [d1, p1] = pts[i];
    const [d2, p2] = pts[i + 1];
    if (d1 < days && days < d2)
      return Math.round((p1 + ((p2 - p1) * (days - d1)) / (d2 - d1)) * 100) / 100;
  }
  return null;
};

const isoKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const PLACEMENT_META: Record<string, { icon: any; name: string; desc: string }> = {
  homepage_banner: { icon: LayoutTemplate, name: "Homepage Banner", desc: "Premium top-of-homepage placement seen by every visitor on every session." },
  in_feed: { icon: Newspaper, name: "In-Feed Native", desc: "A sponsored story card inside the news feed — clearly labeled, natively styled." },
  article_inline: { icon: FileText, name: "Article Inline", desc: "A native unit inside the article body — mid-read, maximum attention." },
  sidebar: { icon: PanelRight, name: "Sidebar Spotlight", desc: "The right-rail unit that lives next to our most-read breaking coverage." },
  audio_brief: { icon: Headphones, name: "Audio Brief Sponsor", desc: "One sponsor per day, mentioned by name at the top of the 60-second Daily Brief — on-site, Telegram and the podcast." },
  sector_mid: { icon: BarChart3, name: "Sector Mid Banner", desc: "A wide display banner between modules on the Crypto, DeFi and Policy sector pages." },
  nav_menu: { icon: LayoutTemplate, name: "Navigation Spotlight", desc: "A compact native strip inside the Markets, Sectors and Latest mega menus — seen on every menu open, site-wide." },
  sector_inline: { icon: Newspaper, name: "Sector In-Feed", desc: "A sponsored story card inside the Latest feed on sector pages — natively styled, sector-adjacent context." },
  research_spotlight: { icon: Newspaper, name: "Research Spotlight", desc: "The premium card beside the Featured Analysis in the homepage Research & Deep Dives section — sponsored-article styling, seen by every homepage visitor." },
  premium_insight: { icon: LayoutTemplate, name: "Premium Insight", desc: "The cinematic intelligence card beneath Trending Now in the homepage More Stories section — full-bleed creative with clear sponsored disclosure." },
};

const STATUS_STYLE: Record<string, string> = {
  approved: "bg-green-500/15 text-green-400 border-green-500/30",
  pending_review: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  changes_requested: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  draft_unpaid: "bg-white/5 text-gray-400 border-white/20",
};

const validateCreativeFile = (f: File, spec: any, placementName: string): Promise<string | null> =>
  new Promise((resolve) => {
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(f.type))
      return resolve("Only PNG, JPG, WebP or GIF files are accepted.");
    const maxMb = f.type === "image/gif" ? 5 : 3;
    if (f.size > maxMb * 1024 * 1024) return resolve(`File must be under ${maxMb}MB.`);
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (!spec) return resolve(null);
      const aspect = img.width / img.height;
      if (img.width < spec.min_width || aspect < spec.aspect_min || aspect > spec.aspect_max)
        return resolve(`Wrong size for ${placementName} — it needs a ${spec.label}, minimum ${spec.min_width}px wide. Yours is ${img.width}×${img.height}.`);
      resolve(null);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve("That file is not a readable image."); };
    img.src = url;
  });

const EMPTY = { name: "", advertiser: "", placement: "sidebar", duration_days: 7, exclusive: false, target_url: "", hours_per_day: 24, start_hour: 9, sponsor_line: "" };

const HOUR_OPTIONS = [
  { h: 24, label: "Full day", sub: "24h/day" },
  { h: 12, label: "Half day", sub: "12h/day" },
  { h: 8, label: "8 hours", sub: "8h/day" },
  { h: 4, label: "4 hours", sub: "4h/day" },
];

const fmtHour = (h: number) => `${String(h).padStart(2, "0")}:00`;

const ResubmitCreative = ({ order, specs }: { order: any; specs: any }) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);

  const onPick = async (f: File | null) => {
    if (!f) return;
    const spec = specs?.[order.placement];
    const err = await validateCreativeFile(f, spec, PLACEMENT_META[order.placement]?.name || order.placement);
    if (err) { toast.error(err); if (inputRef.current) inputRef.current.value = ""; return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("placement", order.placement);
      const up = await api.post("/uploads/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/ad-orders/${order.id}/resubmit`, { image_url: up.data.image_url });
      toast.success("New creative submitted — your campaign is back in review");
      queryClient.invalidateQueries({ queryKey: ["my-ad-orders"] });
    } catch (e) { toast.error(apiError(e)); } finally {
      setSending(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-2 bg-amber-500/5 border border-amber-500/30 rounded-lg px-4 py-3"
      data-testid={`changes-requested-box-${order.id}`}>
      <p className="text-amber-400 text-xs font-semibold mb-1">Our review team requested a change:</p>
      <p className="text-gray-300 text-sm leading-relaxed">{order.note}</p>
      <p className="font-mono text-[10px] text-gray-500 mt-2">
        Required: {specs?.[order.placement]?.label ?? "correct placement size"} · your booking and payment stay reserved
      </p>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid={`resubmit-file-${order.id}`} />
      <Button size="sm" disabled={sending} onClick={() => inputRef.current?.click()}
        data-testid={`resubmit-button-${order.id}`}
        className="mt-3 bg-amber-500 text-black font-bold hover:bg-amber-400 h-8 text-xs">
        <Upload className="h-3.5 w-3.5 mr-1.5" /> {sending ? "Submitting…" : "Upload fixed creative"}
      </Button>
    </div>
  );
};

export default function Advertise() {
  useSEO("Advertise on NewsLayer", "Self-serve advertising on NewsLayer: 10 placements, 7 to 90-day flights, standard rotation or 24/7 exclusive takeover.");
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [terms, setTerms] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [statsFor, setStatsFor] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: pricing } = useQuery({
    queryKey: ["ads-pricing"],
    queryFn: async () => (await api.get("/ads-pricing")).data,
  });
  const { data: specs } = useQuery({
    queryKey: ["ads-specs"],
    queryFn: async () => (await api.get("/ads-specs")).data,
  });
  const { data: trafficHours } = useQuery({
    queryKey: ["ads-traffic-hours"],
    queryFn: async () => (await api.get("/ads-traffic-hours")).data,
    staleTime: 300000,
  });
  const { data: avail } = useQuery({
    queryKey: ["ads-availability", form.placement],
    queryFn: async () => (await api.get(`/ads-availability?placement=${form.placement}`)).data,
    staleTime: 60 * 1000,
  });
  const { data: mine } = useQuery({
    queryKey: ["my-ad-orders"],
    queryFn: async () => (await api.get("/ad-orders/me")).data,
    enabled: !!user,
  });

  const availMap = useMemo(
    () => Object.fromEntries((avail?.days || []).map((d: any) => [d.date, d])),
    [avail]);
  const maxSlots = avail?.max_rotation_slots || 12;

  const dayBlocked = (d: Date) => {
    const info = availMap[isoKey(d)];
    if (!info) return false;
    if (info.hours && info.excl) {
      for (let h = form.start_hour; h < form.start_hour + form.hours_per_day && h < 24; h++) {
        const cnt = info.hours[h] || 0;
        if (info.excl[h] || cnt >= maxSlots || (form.exclusive && cnt > 0)) return true;
      }
      return false;
    }
    return form.exclusive
      ? info.booked_exclusive || info.standard_count > 0
      : info.booked_exclusive || info.standard_count >= maxSlots;
  };
  const windowBlocked = (d: Date) => {
    for (let i = 0; i < form.duration_days; i++) {
      if (dayBlocked(addDays(d, i))) return true;
    }
    return false;
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const disabledDay = (d: Date) => d < today || d > addDays(today, 179) || windowBlocked(d);

  useEffect(() => {
    if (startDate && windowBlocked(startDate)) {
      setStartDate(undefined);
      toast.info("Your selected start date is no longer available for this configuration");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.placement, form.duration_days, form.exclusive, form.hours_per_day, form.start_hour, availMap]);

  const durations: number[] = pricing?.durations || [7, 14, 30, 60, 90];
  const cfg = pricing?.placements?.[form.placement];
  const basePrice = interpBase(cfg?.prices, form.duration_days);
  const mult = form.exclusive ? (cfg?.exclusive_multiplier || 3) : 1;
  const primeHours: number[] = trafficHours?.prime_hours || [];
  const primePct: number = trafficHours?.prime_premium_pct ?? 25;
  const primeInWindow = form.hours_per_day < 24
    ? Array.from({ length: form.hours_per_day }, (_, i) => form.start_hour + i)
        .filter((h) => h < 24 && primeHours.includes(h))
    : [];
  const primeOverlap = primeInWindow.length;
  const hourShare = form.hours_per_day < 24
    ? (form.hours_per_day + (primePct / 100) * primeOverlap) / 24
    : 1;
  const price = basePrice != null ? Math.round(basePrice * mult * hourShare * 100) / 100 : null;
  const perDay = price != null ? price / form.duration_days : null;
  const day7Rate = cfg?.prices?.["7"] != null ? (cfg.prices["7"] * mult) / 7 : null;
  const savePct = perDay != null && day7Rate && form.duration_days > 7
    ? Math.round((1 - perDay / day7Rate) * 100) : 0;
  const chipDurations = [...durations, 180, 365];
  const sweetSpot = (() => {
    const p365 = interpBase(cfg?.prices, 365);
    if (p365 == null) return null;
    const floorRate = p365 / 365;
    for (const d of chipDurations) {
      const p = interpBase(cfg?.prices, d);
      if (p != null && p / d <= floorRate * 1.1) return d;
    }
    return 365;
  })();
  const sweetRate = sweetSpot != null ? interpBase(cfg?.prices, sweetSpot) : null;
  const sweetPerDay = sweetRate != null && sweetSpot ? (sweetRate * mult) / sweetSpot : null;
  const sweetSavePct = sweetPerDay != null && day7Rate
    ? Math.round((1 - sweetPerDay / day7Rate) * 100) : 0;

  const spec = specs?.[form.placement];
  const isAudio = form.placement === "audio_brief";
  const sponsorOk = form.sponsor_line.trim().length >= 10 && form.sponsor_line.trim().length <= 140;

  const validateCreative = (f: File): Promise<string | null> =>
    validateCreativeFile(f, spec, PLACEMENT_META[form.placement].name);

  const onPickFile = async (f: File | null) => {
    if (!f) { setFile(null); setFileError(null); return; }
    const err = await validateCreative(f);
    if (err) {
      setFile(null);
      setFileError(err);
      toast.error(err);
      if (fileRef.current) fileRef.current.value = "";
    } else {
      setFile(f);
      setFileError(null);
    }
  };

  useEffect(() => {
    if (!file) return;
    validateCreative(file).then((err) => {
      if (err) {
        setFile(null);
        setFileError(err);
        if (fileRef.current) fileRef.current.value = "";
        toast.warning("Your uploaded creative doesn't fit the new placement — please upload a correctly sized one.");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.placement, specs]);

  const valid = form.name.length >= 3 && form.advertiser.length >= 2 && form.target_url.length >= 8 && (isAudio ? sponsorOk : !!file) && price != null && terms;

  const launch = async () => {
    if (!user) { navigate("/login"); return; }
    if (!terms) { toast.error("Please accept the Advertising Terms & Conditions first"); return; }
    setBusy(true);
    try {
      let image_url = "";
      if (!isAudio) {
        const fd = new FormData();
        fd.append("file", file as File);
        fd.append("placement", form.placement);
        const up = await api.post("/uploads/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_url = up.data.image_url;
      }
      const res = await api.post("/ad-orders", {
        name: form.name, advertiser: form.advertiser, placement: form.placement,
        duration_days: form.duration_days, exclusive: form.exclusive,
        starts_on: startDate ? isoKey(startDate) : null,
        target_url: form.target_url, image_url,
        sponsor_line: isAudio ? form.sponsor_line.trim() : undefined,
        accepted_terms: terms,
        hours_per_day: isAudio ? 24 : form.hours_per_day,
        start_hour: isAudio || form.hours_per_day === 24 ? 0 : form.start_hour,
      });
      await startCheckout("ad_order", res.data.order.id);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const payPending = async (o: any) => {
    try { await startCheckout("ad_order", o.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openInvoice = async (o: any) => {
    try {
      const res = await api.get(`/ad-orders/${o.id}/invoice`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) { w.document.write(res.data); w.document.close(); }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || "Invoice is available once the campaign is paid");
    }
  };

  const rebook = async (o: any) => {
    try {
      const res = await api.post(`/ad-orders/${o.id}/rebook`);
      toast.success(res.data.order.discount_pct
        ? `Rebooked with ${res.data.order.discount_pct}% loyalty discount — redirecting to checkout…`
        : "Campaign rebooked — redirecting to checkout…");
      await startCheckout("ad_order", res.data.order.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  // One-click rebook deep link from the flight-ended email (?rebook={order_id})
  const [searchParams, setSearchParams] = useSearchParams();
  const rebookFired = useRef(false);
  useEffect(() => {
    const rid = searchParams.get("rebook");
    if (!rid || rebookFired.current || authLoading) return;
    if (!user) {
      rebookFired.current = true;
      toast.info("Sign in with your advertiser account to rebook your campaign in one click");
      return;
    }
    if (!mine) return;
    rebookFired.current = true;
    setSearchParams({}, { replace: true });
    const order = (mine || []).find((o: any) => o.id === rid);
    if (order) rebook(order);
    else toast.error("We couldn't find that campaign on your account");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, authLoading, mine]);

  return (
    <div data-testid="advertise-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
        <TrendingUp className="h-4 w-4" /> Self-serve advertising
      </div>
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl leading-tight max-w-3xl">
        Put your brand in front of crypto's most engaged readers.
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">
        Ten placements, flights from 7 to 90 days, flat pricing — the longer you run, the less you pay per day.
        Standard campaigns rotate in 15-second slots with up to 480 impressions a day; go
        <span className="text-white font-semibold"> 24/7 Exclusive</span> to own a spot outright.
      </p>

      {/* Placement picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {Object.entries(PLACEMENT_META).map(([key, p]) => (
          <div key={key} data-testid={`placement-card-${key}`}
            className={`bg-[#121212] border rounded-xl p-5 cursor-pointer transition-colors ${
              form.placement === key ? "border-[#00E5FF]/60" : "border-white/10 hover:border-[#7C3AED]/50"}`}
            onClick={() => setForm({ ...form, placement: key })}>
            <p.icon className="h-6 w-6 text-[#7C3AED] mb-3" />
            <h3 className="font-heading font-bold text-white text-base">{p.name}</h3>
            <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{p.desc}</p>
            <p className="font-mono text-xs text-[#00E5FF] mt-3">
              from ${pricing?.placements?.[key]?.prices?.["7"] ?? "—"} / 7 days
            </p>
          </div>
        ))}
      </div>

      <AdSpecSheet specs={specs} active={form.placement}
        onSelect={(key) => setForm({ ...form, placement: key })} />

      {/* Duration + exclusive + price calculator */}
      <div className="mt-8 bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="price-calculator">
        <h2 className="font-heading font-bold text-white text-lg mb-1.5">Flight length</h2>
        <p className="text-gray-500 text-xs mb-5">
          Slide to any flight from 1 to 365 days — the price updates live. Longer flights earn a better daily rate.
        </p>
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
          <span data-testid="flight-days-value" className="font-mono text-2xl font-bold text-white">
            {form.duration_days} day{form.duration_days === 1 ? "" : "s"}
          </span>
          <span className="font-mono text-sm">
            <span className="text-[#00E5FF] font-bold text-lg" data-testid="flight-total-price">
              {price != null ? `$${Math.round(price).toLocaleString()}` : "—"}
            </span>
            {perDay != null && (
              <span className="text-gray-400 ml-2" data-testid="flight-per-day">
                ${perDay < 100 ? perDay.toFixed(2) : Math.round(perDay).toLocaleString()}/day
              </span>
            )}
            {savePct > 0 && (
              <span className="ml-2 text-green-400 text-xs" data-testid="flight-save-pct">
                −{savePct}% vs 7-day rate
              </span>
            )}
          </span>
        </div>
        <div className="relative">
          {sweetSpot != null && sweetSpot > 1 && (
            <div className="absolute -top-1.5 z-10 pointer-events-none"
              style={{ left: `${(((sweetSpot - 1) / 364) * 100).toFixed(2)}%` }}
              data-testid="flight-sweet-spot-marker">
              <div className="h-2 w-2 rounded-full bg-[#4ADE80] border border-[#0B0B0B] -translate-x-1/2 shadow-[0_0_8px_rgba(74,222,128,0.7)]" />
            </div>
          )}
          <Slider value={[form.duration_days]} min={1} max={365} step={1}
            onValueChange={([v]) => setForm({ ...form, duration_days: v })}
            data-testid="flight-length-slider"
            className="[&_[role=slider]]:bg-[#00E5FF] [&_[role=slider]]:border-[#00E5FF] [&_.bg-primary]:bg-[#00E5FF]" />
        </div>
        <div className="flex justify-between font-mono text-[9px] uppercase tracking-wider text-gray-500 mt-2">
          <span>1 day</span><span>3 months</span><span>6 months</span><span>1 year</span>
        </div>
        {sweetSpot != null && sweetPerDay != null && (
          <p className="flex items-center gap-1.5 text-xs mt-3" data-testid="flight-sweet-spot">
            <Zap className={`h-3.5 w-3.5 shrink-0 ${form.duration_days >= sweetSpot ? "text-[#4ADE80]" : "text-[#FBBF24]"}`} />
            {form.duration_days >= sweetSpot ? (
              <span className="text-[#4ADE80]">
                You've hit the sweet spot — you're locked into our best daily rate tier.
              </span>
            ) : (
              <span className="text-gray-400">
                Sweet spot: <button type="button" data-testid="flight-sweet-spot-jump"
                  onClick={() => setForm({ ...form, duration_days: sweetSpot })}
                  className="text-[#FBBF24] font-semibold underline decoration-dotted underline-offset-2 hover:text-[#F59E0B]">
                  {sweetSpot} days</button>{" "}
                gets you our best daily rate — ${sweetPerDay < 100 ? sweetPerDay.toFixed(2) : Math.round(sweetPerDay).toLocaleString()}/day
                ({sweetSavePct > 0 ? `−${sweetSavePct}% vs 7-day rate` : "best value"})
              </span>
            )}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {chipDurations.map((d) => {
            const p = interpBase(cfg?.prices, d);
            const isSweet = d === sweetSpot;
            return (
              <button key={d} type="button" onClick={() => setForm({ ...form, duration_days: d })}
                data-testid={`duration-pill-${d}`}
                className={`relative rounded-lg border px-3.5 py-2 text-left transition-colors ${
                  form.duration_days === d
                    ? "border-[#00E5FF] bg-[#00E5FF]/10"
                    : isSweet ? "border-[#4ADE80]/50 hover:border-[#4ADE80]" : "border-white/10 hover:border-white/30"}`}>
                {isSweet && (
                  <span data-testid="best-value-badge"
                    className="absolute -top-2 left-2 font-mono text-[8px] font-bold uppercase tracking-wider text-[#0B0B0B] bg-[#4ADE80] rounded px-1 py-px">
                    Best value
                  </span>
                )}
                <span className="block font-mono text-sm font-bold text-white">{d} days</span>
                <span className="block font-mono text-[10px] text-gray-400 mt-0.5">
                  {p != null ? `$${Math.round(p * mult).toLocaleString()}` : "—"}
                </span>
              </button>
            );
          })}
        </div>

        {!isAudio && (
        <div className="mt-6 border-t border-white/10 pt-5" data-testid="daily-schedule">
          <h3 className="flex items-center gap-2 font-heading font-bold text-white text-base mb-1.5">
            <Clock3 className="h-4 w-4 text-[#00E5FF]" /> Daily schedule
          </h3>
          <p className="text-gray-500 text-xs mb-3">
            Own the slot all day, or book just the hours you need — the price scales with your daily hours.
            Our {primeHours.length || 6} busiest reader hours are prime time and carry a +{primePct}% premium.
          </p>
          <div className="flex flex-wrap gap-2">
            {HOUR_OPTIONS.map((o) => (
              <button key={o.h} type="button" data-testid={`hours-pill-${o.h}`}
                onClick={() => setForm({ ...form, hours_per_day: o.h,
                  start_hour: o.h === 24 ? 0 : Math.min(form.start_hour, 24 - o.h) })}
                className={`rounded-lg border px-4 py-2.5 text-left transition-colors ${
                  form.hours_per_day === o.h
                    ? "border-[#00E5FF] bg-[#00E5FF]/10"
                    : "border-white/10 hover:border-white/30"}`}>
                <span className="block font-mono text-sm font-bold text-white">{o.label}</span>
                <span className="block font-mono text-[10px] text-gray-400 mt-0.5">{o.sub}</span>
              </button>
            ))}
          </div>
          {form.hours_per_day < 24 && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="font-mono text-xs text-gray-400">Daily window (ET):</span>
              <select value={form.start_hour} data-testid="start-hour-select"
                onChange={(e) => setForm({ ...form, start_hour: Number(e.target.value) })}
                className="bg-[#1A1A1A] border border-white/15 rounded-md px-3 py-1.5 text-sm text-white font-mono">
                {Array.from({ length: 25 - form.hours_per_day }, (_, h) => {
                  const n = Array.from({ length: form.hours_per_day }, (_, i) => h + i)
                    .filter((x) => primeHours.includes(x)).length;
                  return (
                    <option key={h} value={h}>
                      {fmtHour(h)} – {fmtHour(h + form.hours_per_day)}{n > 0 ? ` 🔥 ${n} prime` : ""}
                    </option>
                  );
                })}
              </select>
              <span className="font-mono text-[10px] text-gray-500">
                runs {fmtHour(form.start_hour)}–{fmtHour(form.start_hour + form.hours_per_day)} ET every day of your flight
              </span>
            </div>
          )}
          {form.hours_per_day < 24 && primeOverlap > 0 && (
            <div data-testid="prime-pricing-notice"
              className="mt-3 flex items-start gap-2 bg-[#1a1505] border border-[#F59E0B]/30 rounded-lg px-3.5 py-2.5">
              <Zap className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
              <p className="text-xs text-gray-300 leading-relaxed">
                <span className="font-semibold text-[#F59E0B]">Prime time pricing:</span>{" "}
                {primeOverlap} of your {form.hours_per_day} daily hours
                ({primeInWindow.map((h) => fmtHour(h)).join(", ")} ET) fall in our busiest reader hours.
                Those hours carry a +{primePct}% premium — that's when the most readers see your ad.
              </p>
            </div>
          )}
          <SlotHeatmap availMap={availMap} maxSlots={maxSlots} windowDays={form.duration_days}
            startDate={startDate} traffic={trafficHours?.hours}
            primeHours={primeHours} primePct={primePct}
            hourRange={{ start: form.hours_per_day === 24 ? 0 : form.start_hour, len: form.hours_per_day }}
            onPickHour={(h) => setForm({ ...form, start_hour: h })} />
        </div>
        )}

        <div className="mt-6 border-t border-white/10 pt-5">
          <h3 className="flex items-center gap-2 font-heading font-bold text-white text-base mb-1.5">
            <CalendarDays className="h-4 w-4 text-[#00E5FF]" /> Start date
          </h3>
          <p className="text-gray-500 text-xs mb-3">
            Booked or full dates are grayed out for your current placement, duration, hours and exclusivity — no double booking, ever.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div>
              <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="start-date-trigger"
                className="border-white/15 bg-[#1A1A1A] text-white hover:bg-white/5 font-mono text-sm">
                <CalendarDays className="h-4 w-4 mr-2 text-gray-400" />
                {startDate ? isoKey(startDate) : "Launch on approval (default)"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
              <Calendar mode="single" selected={startDate} onSelect={setStartDate}
                disabled={disabledDay} className="text-white" data-testid="start-date-calendar" />
              <div className="px-3 pb-3">
                <button type="button" onClick={() => setStartDate(undefined)} data-testid="start-date-clear"
                  className="font-mono text-[10px] text-gray-400 hover:text-white underline">
                  Clear — launch as soon as approved
                </button>
              </div>
            </PopoverContent>
          </Popover>
          {startDate && (
            <p className="font-mono text-xs text-gray-400 mt-2" data-testid="flight-window">
              Flight: {isoKey(startDate)} → {isoKey(addDays(startDate, form.duration_days))}
            </p>
          )}
            </div>
            <AvailabilityGrid availMap={availMap} maxSlots={maxSlots}
              hourRange={{ start: form.hours_per_day === 24 ? 0 : form.start_hour, len: form.hours_per_day, exclusive: form.exclusive }} />
          </div>
        </div>

        {isAudio ? (
          <div className="flex items-start gap-3 mt-6 bg-[#151022] border border-[#7C3AED]/40 rounded-lg p-4" data-testid="audio-sponsor-note">
            <Headphones className="h-4 w-4 text-[#A78BFA] shrink-0 mt-0.5" />
            <p className="text-gray-400 text-xs leading-relaxed max-w-xl">
              <span className="text-white font-semibold">Exclusive by nature</span> — one sponsor per day.
              Your brand is mentioned by name at the top of every Daily Brief during your flight, heard on-site,
              on Telegram and on the podcast feed.
            </p>
          </div>
        ) : (
        <div className="flex items-start justify-between gap-4 mt-6 bg-[#1a1505] border border-[#F59E0B]/30 rounded-lg p-4">
          <div>
            <p className="flex items-center gap-1.5 text-white font-semibold text-sm">
              <Zap className="h-4 w-4 text-[#F59E0B]" /> 24/7 Exclusive takeover
            </p>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed max-w-md">
              Standard campaigns share the spot in rotation (15s slots, up to 480 impressions/day).
              Exclusive means your ad owns this placement around the clock — no rotation, no caps.
              {cfg ? ` ${cfg.exclusive_multiplier}× the standard rate.` : ""}
            </p>
          </div>
          <Switch checked={form.exclusive} data-testid="exclusive-toggle"
            onCheckedChange={(v) => setForm({ ...form, exclusive: v })} />
        </div>
        )}

        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-6 border-t border-white/10 pt-5">
          <p className="font-mono text-3xl font-bold text-white" data-testid="calculated-price">
            {price != null ? `$${price % 1 ? price.toFixed(2) : price}` : "—"}
          </p>
          <p className="font-mono text-xs text-gray-400">
            {PLACEMENT_META[form.placement].name} · {form.duration_days} days{form.exclusive ? " · 24/7 Exclusive" : isAudio ? " · exclusive daily sponsor" : " · rotation"}
            {form.hours_per_day < 24 ? ` · ${fmtHour(form.start_hour)}–${fmtHour(form.start_hour + form.hours_per_day)} ET daily` : ""}
          </p>
          {perDay != null && (
            <p className="font-mono text-xs text-gray-500">≈ ${perDay.toFixed(2)}/day</p>
          )}
          {savePct > 0 && (
            <p className="font-mono text-xs text-green-400" data-testid="volume-discount">save {savePct}% vs 7-day rate</p>
          )}
          {form.hours_per_day < 24 && primeOverlap > 0 && (
            <p className="font-mono text-xs text-[#F59E0B]" data-testid="prime-premium-line">
              includes +{primePct}% prime premium on {primeOverlap} hr{primeOverlap > 1 ? "s" : ""}/day
            </p>
          )}
        </div>
      </div>

      {/* Creative form */}
      <div className="mt-8 bg-[#0d1117] border border-[#00E5FF]/20 rounded-xl p-6 sm:p-10" data-testid="ad-builder">
        <h2 className="font-heading font-bold text-white text-2xl mb-6">Launch your campaign</h2>
        {!user ? (
          <div data-testid="ads-auth-gate">
            <p className="text-gray-400 mb-4">
              Advertising on NewsLayer requires a free account — register once and you get a campaign
              dashboard with live stats, rebooking and receipts.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate("/signup")} data-testid="ads-signup-button"
                className="bg-[#7C3AED] text-white font-bold hover:bg-[#6b2fd6]">Create free account</Button>
              <Button onClick={() => navigate("/login")} data-testid="ads-signin-button"
                variant="outline" className="border-white/20 text-white hover:bg-white/5">Sign in</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Campaign name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="ads-form-name" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
              <div>
                <Label>Advertiser / brand</Label>
                <Input value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })}
                  data-testid="ads-form-advertiser" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label>Click-through URL</Label>
              <Input value={form.target_url} placeholder="https://yourbrand.com/landing"
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                data-testid="ads-form-target" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            {isAudio ? (
              <div>
                <Label>Sponsor line (read aloud)</Label>
                <Input value={form.sponsor_line} maxLength={140}
                  placeholder='e.g. "Trade crypto with zero fees this month at NovaX dot com."'
                  onChange={(e) => setForm({ ...form, sponsor_line: e.target.value })}
                  data-testid="ads-form-sponsor-line" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
                <p className="font-mono text-[10px] text-gray-500 mt-1.5" data-testid="sponsor-line-hint">
                  {form.sponsor_line.trim().length}/140 · spoken by our narrator right after the intro of every Daily Brief in your flight
                </p>
                {sponsorOk && (
                  <div className="mt-3 bg-[#0B0B0B] border border-white/10 rounded-lg p-4" data-testid="audio-sponsor-preview">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2">
                      Live preview · how it will sound
                    </p>
                    <p className="text-sm text-gray-300 italic leading-relaxed">
                      "This is NewsLayer. Here's your Daily Brief.{" "}
                      <span className="text-white font-medium">
                        Today's brief is presented by {form.advertiser.trim() || "your brand"}. {form.sponsor_line.trim()}
                      </span>{" "}…"
                    </p>
                  </div>
                )}
              </div>
            ) : (
            <div>
              <Label>Banner image</Label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0] || null)} data-testid="ads-form-file" />
              <button onClick={() => fileRef.current?.click()} type="button" data-testid="ads-form-upload-button"
                className={`mt-1 w-full border border-dashed rounded-md p-4 text-sm transition-colors flex items-center justify-center gap-2 ${
                  fileError ? "border-red-500/60 text-red-400" : "border-white/20 text-gray-400 hover:border-[#00E5FF]/50 hover:text-white"}`}>
                <Upload className="h-4 w-4" /> {file ? file.name : "Choose image (PNG / JPG / WebP max 3MB · animated GIF max 5MB)"}
              </button>
              <p className="font-mono text-[10px] text-gray-500 mt-1.5" data-testid="creative-spec-hint">
                Required for {PLACEMENT_META[form.placement].name}: {spec?.label ?? "…"}
              </p>
              {fileError && (
                <p className="text-red-400 text-xs mt-1.5 leading-relaxed" data-testid="creative-error">{fileError}</p>
              )}
              {file && <AdLivePreview file={file} placement={form.placement}
                name={form.name} advertiser={form.advertiser} />}
            </div>
            )}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4" data-testid="ads-terms-box">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                  data-testid="ads-terms-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 bg-transparent accent-[#7C3AED] cursor-pointer" />
                <span className="text-sm text-gray-300 leading-relaxed">
                  I accept the <span className="text-white font-semibold">NewsLayer Advertising Terms &amp; Conditions</span>
                </span>
              </label>
              <details className="mt-2 ml-7">
                <summary className="font-mono text-[10px] uppercase tracking-widest text-[#A78BFA] cursor-pointer hover:text-white transition-colors">
                  Read the terms
                </summary>
                <ul className="text-xs text-gray-400 leading-relaxed mt-2 space-y-1.5 list-disc pl-4">
                  <li>All campaigns are subject to editorial review. If we request changes, you can fix and resubmit your creative without losing your booking. If your campaign receives a final rejection, your payment is automatically refunded in full.</li>
                  <li><span className="text-gray-300 font-medium">NewsLayer does not guarantee results</span> — impressions, clicks, conversions or any business outcome are not guaranteed.</li>
                  <li>Creatives must not promote scams, misleading claims, illegal products, or impersonate NewsLayer editorial content. Ads are always labeled "Sponsored".</li>
                  <li>Live campaigns that are found to violate these standards may be paused without refund.</li>
                  <li>Rotation campaigns share the placement in 15-second slots (up to 480 impressions/day); exclusive campaigns own the slot 24/7 for the booked window.</li>
                  <li>Payments are processed by Stripe; your receipt is emailed by Stripe at checkout.</li>
                </ul>
              </details>
            </div>
            {savePct > 0 && day7Rate != null && price != null && perDay != null && (
              <div className="flex items-center gap-2.5 bg-[#06170d] border border-[#4ADE80]/30 rounded-lg px-4 py-3"
                data-testid="checkout-savings">
                <Zap className="h-4 w-4 text-[#4ADE80] shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">
                  <span className="text-[#4ADE80] font-semibold">
                    You're saving ${Math.max(Math.round(day7Rate * form.duration_days - price), 0).toLocaleString()}
                  </span>{" "}
                  vs the 7-day rate ({savePct}% off · ${perDay < 100 ? perDay.toFixed(2) : Math.round(perDay).toLocaleString()}/day)
                  {sweetSpot != null && form.duration_days >= sweetSpot
                    ? " — best daily rate tier locked in."
                    : "."}
                </p>
              </div>
            )}
            <Button onClick={launch} disabled={busy || !valid} data-testid="ads-launch-button"
              className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] px-8">
              {busy ? "Redirecting to checkout…" : `Pay $${price ?? "—"} & submit for approval`}
            </Button>
          </div>
        )}
      </div>

      {/* My campaigns + live stats */}
      {(mine || []).length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading font-bold text-white text-xl mb-4">My campaigns</h2>
          <div className="space-y-3">
            {mine.map((o: any) => (
              <div key={o.id}>
                <div data-testid={`ad-order-row-${o.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={o.image_url} alt={o.name} className="w-14 h-9 object-cover rounded border border-white/10 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium line-clamp-1">
                        {o.name}
                        {o.exclusive && <span className="ml-2 font-mono text-[9px] text-[#F59E0B] uppercase tracking-widest border border-[#F59E0B]/40 rounded px-1.5 py-0.5">24/7</span>}
                      </p>
                      <p className="font-mono text-[10px] text-gray-500 mt-0.5">
                        {o.placement} · {o.duration_days}d{o.hours_per_day && o.hours_per_day < 24 ? ` · ${fmtHour(o.start_hour || 0)}–${fmtHour((o.start_hour || 0) + o.hours_per_day)} ET` : ""} · ${o.price_usd}{o.discount_pct ? ` (−${o.discount_pct}% loyalty)` : ""} · {timeAgo(o.created_at)}
                        {o.status === "approved" && o.ends_at && (
                          o.ended
                            ? <span className="text-red-400 ml-1.5">· ended</span>
                            : o.days_remaining <= 3
                              ? <span className="text-yellow-400 ml-1.5" data-testid={`ending-soon-${o.id}`}>· ends in {o.days_remaining}d</span>
                              : <span className="ml-1.5">· {o.days_remaining}d left</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={`${STATUS_STYLE[o.status] || "border-white/20 text-gray-300"} font-mono text-[10px] uppercase`}>
                      {o.status === "draft_unpaid" ? "payment pending" : o.status.replace("_", " ")}
                    </Badge>
                    {o.status === "draft_unpaid" && (
                      <Button size="sm" onClick={() => payPending(o)} data-testid={`ad-order-pay-${o.id}`}
                        className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] h-7 text-xs">Pay ${o.price_usd}</Button>
                    )}
                    {o.paid && (
                      <Button size="sm" variant="outline" data-testid={`ad-order-invoice-${o.id}`}
                        onClick={() => openInvoice(o)}
                        className="border-white/15 text-gray-300 hover:bg-white/5 h-7 text-xs">
                        <FileText className="h-3.5 w-3.5 mr-1" /> Invoice
                      </Button>
                    )}
                    {o.status === "approved" && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`ad-order-stats-${o.id}`}
                          onClick={() => setStatsFor(statsFor === o.id ? null : o.id)}
                          className="border-white/15 text-gray-300 hover:bg-white/5 h-7 text-xs">
                          <BarChart3 className="h-3.5 w-3.5 mr-1" /> Stats
                        </Button>
                        <Button size="sm" onClick={() => rebook(o)} data-testid={`ad-order-rebook-${o.id}`}
                          className={`h-7 text-xs font-bold ${o.ended || (o.days_remaining != null && o.days_remaining <= 3)
                            ? "bg-[#F59E0B] text-black hover:bg-[#d98b09]"
                            : "bg-white/5 text-gray-300 border border-white/15 hover:bg-white/10"}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Rebook
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {o.status === "approved" && o.renewal_discount_pct && (
                  <div data-testid={`renewal-nudge-${o.id}`}
                    className="mt-2 flex flex-wrap items-center justify-between gap-3 bg-[#F59E0B]/5 border border-[#F59E0B]/40 rounded-lg px-4 py-3">
                    <p className="text-sm text-gray-200">
                      <span className="text-[#FBBF24] font-bold">
                        {o.ended ? "Your campaign just ended" : `Ends in ${o.days_remaining} day${o.days_remaining === 1 ? "" : "s"}`}
                      </span>{" "}
                      — rebook the same flight in one click and keep your slot.
                    </p>
                    <Button size="sm" onClick={() => rebook(o)} data-testid={`renewal-rebook-${o.id}`}
                      className="bg-[#F59E0B] text-black font-bold hover:bg-[#d98b09] h-8 text-xs">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Rebook &amp; save {o.renewal_discount_pct}%
                    </Button>
                  </div>
                )}
                {o.status === "changes_requested" && <ResubmitCreative order={o} specs={specs} />}
                {statsFor === o.id && <CampaignStats orderId={o.id} />}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-14">
        <NewsletterCTA sourceType="advertise"
          heading="Prefer a managed campaign?"
          sub="Drop your email and we'll reply within 24 hours with placements, pricing and availability." />
      </div>
      <p className="font-mono text-[10px] text-gray-600 mt-6">
        Looking to publish an announcement instead? Try the <Link to="/pr" className="text-gray-500 underline">$99 PR service</Link>.
      </p>
    </div>
  );
}
